/**
 * Settings: one window, pages down the side. General, Playback and Library are
 * Nova's own; plugins add pages after them. Help pages (shortcuts, search
 * syntax, writing a plugin, about) live here too, so they open in the same
 * place every time.
 */

import { ClipTypesSettings } from "../clips/ClipTypesSettings";
import { FfmpegPanel } from "./FfmpegNotice";
import { BpmComparePage, DeepRhythmPanel, describeHealth } from "./DeepRhythm";
import { deleteGroup, fetchAnalysisHealth, fetchAnalysisOptions, fetchGroups, restartAnalysis, saveAnalysisOptions, type AnalysisHealth, type AnalysisOptions, type TrackGroup } from "../api";
import { toast } from "./Toast";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useApp } from "../core/app";
import { useJobs } from "../core/useJobs";
import { player, usePlayer } from "../core/player";
import { useRegistry } from "../core/registry";
import { Settings as LibrarySettings } from "../Settings";
import { Icon } from "./Icons";
import { MOD } from "./menus";
import { UsersSettings } from "./UsersSettings";
import { WebSettings } from "./WebSettings";
import { useMay, useSession } from "../core/session";
import { VERSION } from "./StatusBar";
import { OutputPicker } from "./OutputPicker";
import { usePref } from "../core/prefs";
import { AUTO_CHECK_PREF, CHANNEL_PREF, INSTALL_PREF, applyInstallWhen, checkAndTell, defaultChannel, type InstallWhen } from "../core/updates";
import { UpdatePanel } from "./UpdateProgress";
import { USER_NAME_PREF } from "../core/needsEdit";
import { isDesktop } from "../platform";

interface Page {
  id: string;
  label: string;
  group: "settings" | "help";
  render: () => ReactNode;
  /** With accounts on, shown only to a role with this (core/session.ts); a list: any of them. */
  permission?: string | string[];
}

export function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="nv-setrow">
      <div>
        <b>{label}</b>
        {hint && <span>{hint}</span>}
      </div>
      <div className="nv-setrow-control">{children}</div>
    </div>
  );
}

export function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="nv-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} role="radio" aria-checked={value === o.id} className={value === o.id ? "on" : ""} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function General() {
  const app = useApp();
  const { layouts } = useRegistry();
  const [name, setName] = usePref(USER_NAME_PREF, "");
  // Signed in, a flag names the account (the engine sees to it): no name to type here.
  const signedIn = !!useSession().user;
  return (
    <>
      <Row label="Theme" hint="Follow system matches your computer's light or dark setting.">
        <Seg label="Theme" value={app.theme.choice} onChange={app.theme.setChoice} options={[{ id: "light", label: "Light" }, { id: "dark", label: "Dark" }, { id: "system", label: "System" }]} />
      </Row>
      <Row label="Layout" hint={layouts.find((l) => l.id === app.layout)?.description}>
        {app.layoutLocked
          ? <span className="nv-dim">Locked by a plugin (Radio's station mode, say)</span>
          : <Seg label="Layout" value={app.layout} onChange={app.setLayout} options={layouts.map((l) => ({ id: l.id, label: l.label }))} />}
      </Row>
      <Row label="Key notation" hint="Camelot codes show which keys mix; note names are what musicians say.">
        <Seg label="Key notation" value={app.notation} onChange={app.setNotation} options={[{ id: "camelot", label: "8A" }, { id: "musical", label: "A minor" }]} />
      </Row>
      {!signedIn && (
        <Row label="Your name" hint="Shown beside songs you flag for editing, so others know who to ask. Kept on this computer only.">
          <input className="nv-input" value={name} maxLength={80} placeholder="Optional" onChange={(e) => setName(e.target.value)} aria-label="Your name" />
        </Row>
      )}
    </>
  );
}

function Playback() {
  const p = usePlayer();
  return (
    <>
      <Row label="Output" hint="The sound device songs play on. With Radio on, this is the preview at the desk (headphones, say); On air has its own in Settings → Radio.">
        <OutputPicker name="main" label="Output device" />
      </Row>
      <Row label="Crossfade" hint="How long one song plays under the next. Songs with a mix out set hand over there.">
        <Seg label="Crossfade" value={String(p.crossfade)} onChange={(v) => player.setCrossfade(Number(v))} options={["0", "3", "6", "9", "12"].map((s) => ({ id: s, label: s === "0" ? "Off" : `${s} s` }))} />
      </Row>
      <Row label="Volume levelling" hint="Plays every song at the same loudness (−14 LUFS), using Nova's measurements. Never boosts more than 6 dB, nor past 1 dB below the song's loudest sample, so a boost cannot distort.">
        <label className="nv-toggle">
          <input type="checkbox" role="switch" checked={p.levelling} onChange={(e) => player.setLevelling(e.target.checked)} />
          <i aria-hidden="true" />
        </label>
      </Row>
      <Row label="Volume">
        <input type="range" min={0} max={1} step={0.01} value={p.volume} aria-label="Volume" style={{ ["--fill" as string]: `${p.volume * 100}%` }} onChange={(e) => player.setVolume(Number(e.target.value))} />
      </Row>
    </>
  );
}

/** How much each song's analysis works out: fast or deep, and the flow mixer's spectrum extra. */
function AnalysisChoices({ offline }: { offline: boolean }) {
  const [opts, setOpts] = useState<AnalysisOptions | null>(null);
  useEffect(() => {
    if (offline) return;
    void fetchAnalysisOptions().then(setOpts);
  }, [offline]);
  if (!opts) return null;
  return (
    <>
    <Row label="Analysis" hint="Fast reads each song's BPM off a quick drum estimate. Deep separates drums from melody first, which takes about two more seconds a song. Applies from the next song analysed; songs already analysed keep their values until analysed again.">
      <Seg label="Analysis" value={opts.depth} onChange={(v) => void saveAnalysisOptions({ depth: v }).then((r) => r && setOpts(r))}
        options={[{ id: "fast", label: "Fast" }, { id: "deep", label: "Deep" }]} />
    </Row>
    <Row label="Cue points" hint="Where each song starts and ends when Nova finds them itself. Nova's own looks for the first and last sound above silence. Liquidsoap-style sets them from the song's own loudness, the way Liquidsoap's autocue does: it starts where the song comes within 34 dB of its level, ends where it falls 42 dB below it, and finds where the next song should come in, so with a crossfade each song overlaps the next for as long as its own ending needs (up to 6 s) instead of the fixed crossfade. Switching applies to every song at once; songs analysed before this choice existed only have Nova's until analysed again. Cue points you set by hand always win.">
      <Seg label="Cue points" value={opts.cues} onChange={(v) => void saveAnalysisOptions({ cues: v }).then((r) => r && setOpts(r))}
        options={[{ id: "nova", label: "Nova" }, { id: "autocue", label: "Liquidsoap-style" }]} />
    </Row>
    <Row label="Spectrum bands" hint="Bass, mid and treble for each song: an extra for the flow mixer. Nothing else uses them, so they are worked out only while this is on, from the next song analysed.">
      <label className="nv-toggle">
        <input type="checkbox" role="switch" checked={opts.spectrum} disabled={offline}
          onChange={(e) => void saveAnalysisOptions({ spectrum: e.target.checked }).then((r) => r && setOpts(r))} />
        <i aria-hidden="true" />
      </label>
    </Row>
    </>
  );
}

/** Settings -> Analysis (library.analysis): ffmpeg, DeepRhythm, how deep, how many at once. */
function Analysis() {
  const app = useApp();
  const jobs = useJobs(app.offline);
  // Every number this computer allows (cores but one), not a fixed 4; above "safe" is marked.
  const most = Math.max(1, Math.min(16, jobs.report?.workersMax ?? 4));
  const safe = Math.max(1, Math.min(most, jobs.report?.workersSafe ?? most));
  const [health, setHealth] = useState<AnalysisHealth | null>(null);
  useEffect(() => {
    if (app.offline) return;
    void fetchAnalysisHealth().then(setHealth);
  }, [app.offline]);
  const broken = health?.process.broken;
  return (
    <>
    <FfmpegPanel />
    <DeepRhythmPanel />
    <AnalysisChoices offline={app.offline} />
    {jobs.report && (
      <Row label="Songs analysed at once" hint={`More finishes a big library sooner but keeps the computer busier while you work. Each song keeps about one processor core busy. Up to ${safe} leaves this computer free for everything else; more is marked !. Analysis runs at low priority in its own process, so playback and the window come first either way.`}>
        <Seg label="Songs analysed at once" value={String(jobs.report.workers)} onChange={(v) => void jobs.setWorkers(Number(v))}
          options={Array.from({ length: most }, (_, i) => ({ id: String(i + 1), label: i + 1 > safe ? `${i + 1} !` : String(i + 1) }))} />
      </Row>
    )}
    {jobs.report && jobs.report.workers > safe && (
      <div className="nv-update failed" role="alert">
        <div className="nv-update-head">
          <b>Nova and other apps may become slow or stop responding while songs are analysed.</b>
          <span className="nv-dim">
            {jobs.report.workers} songs at once keeps most of this computer busy ({safe} is the most that leaves room for everything else).
            Analysis finishes sooner, but playback, the window and other programs can stutter until it is done.
          </span>
        </div>
      </div>
    )}
    <Row label="Analysis stuck?" hint="Ends the analysis process, however stuck, and starts a fresh one. Songs it was on go back in the queue; a song that stops it three times is set aside.">
      <button className="nv-btn sm" disabled={app.offline} onClick={() => void restartAnalysis().then((r) => {
        if (r?.error) toast(r.error, "bad");
        else if (r) {
          toast(`Analysis restarted. ${describeHealth(r)}`, "ok");
          setHealth(r);
        }
      })}>Restart analysis</button>
    </Row>
    {broken && (
      <div className="nv-update failed" role="alert">
        <div className="nv-update-head">
          <b>Analysis keeps stopping.</b>
          <span className="nv-dim">It stopped three times in a row (exit code {broken.code ?? "?"}), so Nova stopped restarting it. These are its last words:</span>
        </div>
        <pre className="nv-dr-log">{broken.lines.join("\n") || "It wrote nothing."}</pre>
      </div>
    )}
    </>
  );
}

/**
 * Settings -> Library (settings.manage or library.folders): how names are split
 * (settings.manage), watching the folders (library.folders) and track groups
 * (library.edit). Each part only for a role allowed it.
 */
function Library() {
  const app = useApp();
  const lib = app.library;
  const mayDo = useMay();
  return (
    <>
    {mayDo("library.edit") && <TrackGroups />}
    <LibrarySettings
      embedded
      separators={mayDo("settings.manage")}
      showWatch={mayDo("library.folders")}
      payload={lib.settings}
      watch={lib.status?.watch ?? null}
      onSave={lib.saveSettings}
      onSaveWatch={lib.saveWatch}
      onSweep={lib.sweepNow}
      onClose={app.closeSettings}
    />
    </>
  );
}

/** Nova's track groups (group:Power in a search), each with a Delete. Hidden while there are none. */
function TrackGroups() {
  const app = useApp();
  const [groups, setGroups] = useState<TrackGroup[]>([]);
  const read = () => void fetchGroups().then((r) => setGroups(r.groups));
  useEffect(() => {
    if (!app.offline) read();
  }, [app.offline]);
  if (!groups.length) return null;
  const remove = async (g: TrackGroup) => {
    if (!window.confirm(`Delete the group “${g.name}”?\n\nIts ${g.tracks} song${g.tracks === 1 ? "" : "s"} stay in your library; only the group goes.`)) return;
    const out = await deleteGroup(g.name);
    if (out.error) toast(out.error, "bad");
    else {
      toast(`Deleted the group “${g.name}”.`, "ok");
      read();
    }
  };
  return (
    <Row label="Track groups" hint="Groups songs belong to (search with group:Name). Deleting one takes the songs out of it; the songs stay.">
      <ul className="nv-grouplist">
        {groups.map((g) => (
          <li key={g.id}>
            <span>{g.name} <span className="nv-dim">· {g.tracks}</span></span>
            <button className="nv-iconbtn sm" title={`Delete “${g.name}”`} aria-label={`Delete the group “${g.name}”`} disabled={app.offline} onClick={() => void remove(g)}>
              <Icon name="trash" size={13} />
            </button>
          </li>
        ))}
      </ul>
    </Row>
  );
}

const SHORTCUTS: [string, string][] = [
  ["Space", "Play or pause"],
  [`${MOD}+→ / ${MOD}+←`, "Next / previous song"],
  [`${MOD}+S`, "Shuffle on or off (saves in Flow and the Tag editor)"],
  [`${MOD}+M`, "Mute"],
  [`${MOD}+K`, "Search"],
  ["Enter", "Play the selected song"],
  [`${MOD}+Enter`, "Play the selection next"],
  ["Q", "Add the selection to the queue"],
  ["Del", "Remove the selection from the library"],
  [`${MOD}+A`, "Select every song shown"],
  [`${MOD}+I`, "Edit tags"],
  [`${MOD}+O`, "Add a music folder"],
  [`${MOD}+N`, "New playlist"],
  ["F5", "Rescan the library"],
  [`${MOD}+1 / 2 / 3`, "Switch layout (in the order of the layout buttons)"],
  [`${MOD}+B`, "Show or hide the sidebar"],
  [`${MOD}+J`, "Show or hide the details panel"],
  [`${MOD}+,`, "Settings"],
  ["Esc", "Close a menu, clear the search, clear the selection"],
];

function Shortcuts() {
  return (
    <table className="nv-table nv-shortcuts">
      <tbody>
        {SHORTCUTS.map(([k, v]) => (
          <tr key={k}><td><kbd>{k}</kbd></td><td>{v}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function SearchHelp() {
  const rows: [string, string][] = [
    ["burna boy", "Words search titles, artists and albums"],
    ["genre:amapiano", "A genre (and its sub-genres with genre:amapiano/*)"],
    ['artist:"Tems"', "Any song an artist is credited on, featured too"],
    ["bpm:110..120", "A range. Also bpm:>120, bpm:<90"],
    ["key:8A", "A key, in Camelot or as a note name (key:Am)"],
    ["energylevel:4..5", "Energy, 1 to 5"],
    ["danceability:>0.7", "Danceability, 0 to 1"],
    ["year:1970..1989", "Years"],
    ["-genre:gospel", "A minus sign leaves things out"],
    ["archived:yes", "Archived songs, normally hidden"],
  ];
  return (
    <>
      <p className="nv-help">Type in the search box at the top. Terms combine: every term must match.</p>
      <table className="nv-table nv-shortcuts">
        <tbody>{rows.map(([k, v]) => <tr key={k}><td><code>{k}</code></td><td>{v}</td></tr>)}</tbody>
      </table>
    </>
  );
}

function PluginGuide() {
  return (
    <div className="nv-help">
      <p>A plugin is a folder in Nova's data folder under <code>plugins/</code> (or a Python package with a <code>nova.plugins</code> entry point). It has two optional halves:</p>
      <p><b>plugin.py</b> runs in the engine. It declares itself and registers what it adds:</p>
      <pre>{`PLUGIN = {"id": "hello", "name": "Hello", "version": "1.0.0", "api": 1}

def register(nova):
    nova.route("GET", "greeting", lambda state, q: {"hello": "world"})
    nova.on("library.changed", lambda event: print(event.topic))
    nova.migrate(Path(__file__).parent / "migrations")   # its own tables`}</pre>
      <p><b>ui.js</b> runs in the window. Its default export receives Nova's interface API:</p>
      <pre>{`export default function activate(nova) {
  nova.views.add({ id: "hello", label: "Hello", icon: "plug", section: "plugins",
    render: () => nova.h("div", { className: "nv-view" }, "Hello") });
  nova.trackActions.add({ id: "hello.count", label: "Count", group: "tools",
    run: (tracks) => nova.ui.toast(tracks.length + " songs") });
  nova.menus.add("tools", { id: "hello", label: "Say hello", run: () => nova.ui.toast("Hello") });
  nova.events.on("player.track", (e) => console.log(e.data));
}`}</pre>
      <p>Everything a plugin adds is removed when it is turned off. The full guide is <code>docs/PLUGINS.md</code>; a working example is <code>examples/plugins/hello</code>.</p>
    </div>
  );
}

function About() {
  const app = useApp();
  return (
    <div className="nv-help">
      <p><b>Nova {VERSION}</b> — a music player for African music at large, with analysis you can check and curves you can feel.</p>
      <p>Engine: {app.offline ? "not running" : `connected · ${app.library.status?.genres ?? 0} genres in the taxonomy · ${app.library.total.toLocaleString()} songs`}.</p>
      <p>Your files are the source of truth; Nova's database can always be rebuilt from them. Nova never writes into your music folders except the tags you choose to edit.</p>
      {isDesktop() && <Updates />}
    </div>
  );
}

/** Updates come from github.com/blacdev/Nova-Release, signed (src-tauri/src/updates.rs). */
function Updates() {
  const [auto, setAuto] = usePref(AUTO_CHECK_PREF, true);
  const [channel, setChannel] = usePref(CHANNEL_PREF, defaultChannel());
  const [when, setWhen] = usePref<InstallWhen>(INSTALL_PREF, "manual");
  return (
    <>
      <Row label="Updates" hint="New versions come from Nova's release page and are installed only if their signature checks out.">
        <button className="nv-btn" onClick={() => void checkAndTell()}>Check for updates</button>
      </Row>
      <UpdatePanel />
      <Row label="Install a downloaded update" hint="When I click: you choose the moment (Restart and install). Right away: Nova restarts as soon as the download is checked. When Nova closes: installed as you quit, ready next time.">
        <Seg label="Install a downloaded update" value={when}
          onChange={(v) => { setWhen(v); window.setTimeout(() => void applyInstallWhen(), 0); }}
          options={[{ id: "manual", label: "When I click" }, { id: "auto", label: "Right away" }, { id: "exit", label: "When Nova closes" }]} />
      </Row>
      <Row label="Updates from" hint={`This is Nova ${VERSION}. Stable: versioned releases only. Staging: the test build made on every change to staging.`}>
        <Seg label="Update channel" value={channel} onChange={setChannel} options={[{ id: "stable", label: "Stable releases" }, { id: "staging", label: "Staging test builds" }]} />
      </Row>
      <Row label="Check when Nova starts">
        <label className="nv-toggle">
          <input type="checkbox" role="switch" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
          <i aria-hidden="true" />
        </label>
      </Row>
    </>
  );
}

export function SettingsDialog() {
  const app = useApp();
  const { settingsPages } = useRegistry();
  const ref = useRef<HTMLDivElement>(null);
  const all: Page[] = [
    { id: "general", label: "General", group: "settings", render: () => <General /> },
    { id: "playback", label: "Playback", group: "settings", render: () => <Playback /> },
    { id: "analysis", label: "Analysis", group: "settings", render: () => <Analysis />, permission: "library.analysis" },
    { id: "library", label: "Library", group: "settings", render: () => <Library />, permission: ["settings.manage", "library.folders"] },
    { id: "clips", label: "Clips", group: "settings", render: () => <ClipTypesSettings />, permission: "clips.edit" },
    { id: "bpm-compare", label: "BPM comparison", group: "settings", render: () => <BpmComparePage />, permission: "library.analysis" },
    // With accounts off anyone may open it (it is where they are turned on); on, only who manages users.
    { id: "users", label: "Users & roles", group: "settings", render: () => <UsersSettings />, permission: "users.manage" },
    { id: "web", label: "Remote Connections", group: "settings", render: () => <WebSettings />, permission: "settings.remote" },
    ...settingsPages.map((p) => ({ id: p.id, label: p.label, group: "settings" as const, render: p.render, permission: p.permission })),
    { id: "shortcuts", label: "Keyboard shortcuts", group: "help", render: () => <Shortcuts /> },
    { id: "search", label: "Search syntax", group: "help", render: () => <SearchHelp /> },
    { id: "plugin-guide", label: "Writing a plugin", group: "help", render: () => <PluginGuide /> },
    { id: "about", label: "About Nova", group: "help", render: () => <About /> },
  ];
  const mayDo = useMay();
  // Only the pages this person's role reaches (all of them with accounts off).
  const pages = all.filter((p) => !p.permission || [p.permission].flat().some((x) => mayDo(x)));
  const current = pages.find((p) => p.id === app.settingsPage) ?? pages[0];

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        app.closeSettings();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [app]);

  return (
    <div className="nv-modal" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && app.closeSettings()}>
      <div className="nv-dialog nv-settings" role="dialog" aria-modal="true" aria-label="Settings" tabIndex={-1} ref={ref}>
        <nav className="nv-settings-nav" aria-label="Settings pages">
          <h6>Settings</h6>
          {pages.filter((p) => p.group === "settings").map((p) => (
            <button key={p.id} className={`nv-nav ${current.id === p.id ? "on" : ""}`} onClick={() => app.openSettings(p.id)}>{p.label}</button>
          ))}
          <h6>Help</h6>
          {pages.filter((p) => p.group === "help").map((p) => (
            <button key={p.id} className={`nv-nav ${current.id === p.id ? "on" : ""}`} onClick={() => app.openSettings(p.id)}>{p.label}</button>
          ))}
        </nav>
        <section className="nv-settings-page">
          <header>
            <h2>{current.label}</h2>
            <button className="nv-iconbtn" title="Close (Esc)" onClick={app.closeSettings}><Icon name="close" /></button>
          </header>
          <div className="nv-settings-body">{current.render()}</div>
        </section>
      </div>
    </div>
  );
}
